/**
 * @Description 相机类，包括正交相机与透视相机
 * @Author iCEA
 * @Date 2020-10-29
 * @Version 1.0
 * @todo 1、2D正交相机的一系列交互
 */

import * as THREE from 'three'
import { EventEmitter } from 'events/events'
import { Events } from './Events'
import { BuildingTagSet } from './BuildingTag'
import { config } from './config'
import { Vector3 } from 'three'

class Camera {
  /**
   * Camera类构造函数
   * @param {object} settings setting对象
   */
  constructor (settings) {
    /**
     * @type {THREE.Camera} THREE相机对象
     */
    this.camera = null
    /**
     * @type {number} 相机经度
     */
    this.lon = -90
    /**
     * @type {number} 相机纬度
     */
    this.lat = 0
    /**
     * @type {number} 俯仰角
     */
    this.phi = 0
    /**
     * @type {number} 偏航角
     */
    this.theta = 0
    /**
     * @type {number} 缩放参数
     */
    this.zoom = 30000
    /**
     * @type {THREE.Vector3} 相机看向的位置
     */
    this.target = new THREE.Vector3()
    /**
     * @type {number} 相机当前点位下标
     */
    this._cameraCurrentIndex = 0
    /**
     * @type {number} 相机目标点位下标
     */
    this.cameraDestinationIndex = 0
    /**
     * @type {boolean} 是否限制上下视角范围（纬度范围）用以显示模糊区域
     */
    this.isLimitAngle = settings.isLimitAngle

    /**
     * @type {boolean} 是否无限制
     */
    this.isNoLimit = false

    /**
     * @type {boolean} 相机模式，true为全景模式，false为上帝模式
     */
    this.cameraMode = settings.modelIndexEnterType === 3
    /**
     * @type {boolean} god to view 标志，表示正在切换至全景视角
     */
    this.g2vsign = false
    /**
     * @type {boolean} view to god 标志，表示正在切换至上帝视角
     */
    this.v2gsign = false

    // 相机初始化时获取宽高参数
    /**
     * @type {Element} canvas父级div块对象
     */
    this.d = document.getElementById('model-demo')
    /**
     * @type {number} div块宽
     */
    this.width = this.d.clientWidth || this.d.offsetWidth
    /**
     * @type {number} div块高
     */
    this.height = this.d.clientHeight || this.d.offsetHeight

    /**
     * @type {number} 正交相机视野
     */
    this.orthRange = 25000 / this.height
    /**
     * @type {boolean} 相机类型，true是透视相机，false是正交相机
     */
    this.cameraType = true
    /**
     * @type {THREE.Camera} 透视相机
     */
    this.persCamera = new THREE.PerspectiveCamera(65, this.width / this.height, 500, 1000000)
    /**
     * @type {THREE.Camera} 正交相机
     */
    this.orthCamera = new THREE.OrthographicCamera(-this.width * this.orthRange, this.width * this.orthRange, this.height * this.orthRange, -this.height * this.orthRange, 500, 3000000)

    /**
     * @type {EventEmitter} 自定义事件
     */
    this.events = new EventEmitter()

    this.center = new Vector3(0, 0, 0)
  }

  /**
   * 改变现有的相机类型（正交/透视）
   * @param {THREE.Camera} cam
   */
  changeMainCameraTo (cam) {
    if (this.camera !== cam) {
      this.camera = cam
      this.events.emit(Events.MainCameraChange, cam)
    }
  }

  /**
   * 以对象形式，返回相机当前点位下标、经纬度
   * @returns {{CameraPoint:number,Lon:number,Lat:number}} 相机状态信息
   */
  getPose () {
    const Pose = {
      CameraPoint: this.cameraCurrentIndex,
      Lon: this.lon,
      Lat: this.lat
    }
    return Pose
  }

  get cameraCurrentIndex () {
    return this._cameraCurrentIndex
  }

  set cameraCurrentIndex (value) {
    if (value !== this._cameraCurrentIndex) {
      this._cameraCurrentIndex = value
      this.events.emit(Events.IndexChange, value)
    }
  }

  get fov () {
    return this.camera.fov
  }

  set fov (value) {
    if (value !== this.camera.fov) {
      this.camera.fov = value
      this.events.emit(Events.CameraFovChange, value)
    }
  }

  /**
   * 初始化方法
   * @param {THREE.Vector3} pos 相机初始化位置
   * @param {THREE.Vector3} center 相机初始化位置
   */
  initCamera (pos, center) {
    this.center.copy(center)

    if (this.cameraType) { // 相机类型
      this.camera = this.persCamera
    } else {
      this.camera = this.orthCamera
    }

    if (this.cameraMode) { // 初始在全景视角
      this.camera.position.copy(pos)
    } else { // 初始在模型视角
      if (this.cameraType) { // 判断正交/透视相机
        this.target.copy(pos)
      } else {
        this.target.set(0, 0, 0)
        this.lat = 89 // 锁定89纬度
      }

      // 根据lonlat算相机位置
      this.lon += 180
      this.godUpdate()
    }
  }

  /**
   * 更新相机姿态 - 全景视角
   */
  viewUpdate () {
    if (!this.g2vsign) { // 非视角模式变化时，常规全景模式需要限制纬度
      if (this.isNoLimit) {
        this.lat = Math.max(-89, Math.min(89, this.lat))
      } else if (this.isLimitAngle) { // 如果不显示模糊区域就开限制
        this.lat = Math.max(-35, Math.min(35, this.lat))
      } else { // 关闭限制则显示模糊区域
        this.lat = Math.max(-45, Math.min(45, this.lat)) // 需要微调
      }
    }
    this.phi = THREE.MathUtils.degToRad(90 - this.lat)

    this.lon = this.lon % 360

    this.theta = THREE.MathUtils.degToRad(this.lon)

    const ot = this.target.clone()
    // 球坐标系
    this.target.x = Math.sin(this.phi) * Math.cos(this.theta)
    this.target.y = Math.cos(this.phi)
    this.target.z = Math.sin(this.phi) * Math.sin(this.theta)
    this.target.multiplyScalar(1000)
    this.target.x += this.camera.position.x
    this.target.y += this.camera.position.y
    this.target.z += this.camera.position.z
    this.camera.lookAt(this.target)

    ot.sub(this.target)
    if (ot.lengthSq() > 0.1) {
      if (!this.g2vsign) {
        this.events.emit(Events.CameraRotate, this.target, this.camera.position)
      }
    }
  }

  /**
   * 更新相机姿态 - 模型视角（透视相机）
   */
  godUpdate () {
    if (this.cameraType) { // 如果是透视相机则限制纬度到80 否则不设限制锁定89纬度。
      this.lat = Math.max(30, Math.min(80, this.lat))
    }
    this.phi = THREE.MathUtils.degToRad(90 - this.lat)

    this.lon = this.lon % 360

    this.theta = THREE.MathUtils.degToRad(this.lon)
    // 球坐标系
    this.camera.position.x = Math.sin(this.phi) * Math.cos(this.theta)
    this.camera.position.y = Math.cos(this.phi)
    this.camera.position.z = Math.sin(this.phi) * Math.sin(this.theta)

    // TODO: 这里恢复限制，同时考虑到2D视角下的限制实现方案
    if (this.cameraType) {
      this.zoom = this.zoom > this.camera.near + 14500 ? (this.zoom < this.camera.far / 10 - 50000 ? this.zoom : this.camera.far / 10 - 50000) : this.camera.near + 14500
    }

    this.camera.position.multiplyScalar(this.zoom)
    this.camera.position.add(this.target)
    this.camera.lookAt(this.target)
  }

  /**
   * 模型视角下相机平移，更新相机target参数
   *
   * @param {number} movementX 平移屏幕X轴幅度
   * @param {number} movementY 平移屏幕Y轴幅度
   */
  panning (movementX, movementY) {
    const alpha = THREE.MathUtils.degToRad(this.lon)
    const deltaX = movementX * Math.sin(-alpha) - movementY * Math.cos(alpha)
    const deltaZ = movementX * Math.cos(alpha) - movementY * Math.sin(alpha)
    this.target.x += deltaX * this.zoom * 0.0015
    this.target.z += deltaZ * this.zoom * 0.0015

    const off = this.target.clone().sub(this.center)

    if (off.x < -config.GodMode.PanningLimit) {
      this.target.x = this.center.x - config.GodMode.PanningLimit
    } else if (off.x > config.GodMode.PanningLimit) {
      this.target.x = this.center.x + config.GodMode.PanningLimit
    }

    if (off.z < -config.GodMode.PanningLimit) {
      this.target.z = this.center.z - config.GodMode.PanningLimit
    } else if (off.z > config.GodMode.PanningLimit) {
      this.target.z = this.center.z + config.GodMode.PanningLimit
    }
  }
}

export {
  Camera
}
